// Renders WearState on the car (RALLY_GOAL.md §5 P1-3): dirt shading on the paint, the flat
// exterior parts and the wheels (heaviest low down and at the back, patchy), a dirty film on the
// glass with the wiper arcs left clean, and dents (inward displacement plus scuffed paint).
import * as THREE from 'three/webgpu';
import { uniform, uniformArray, vec3, vec4, float, positionLocal, normalLocal, mix, smoothstep, clamp, Loop, Fn, mx_noise_float, length, min, step, attribute, frontFacing, select } from 'three/tsl';
import type { CarModel } from './car-model';
import { MAX_DENTS, WearState, type BodyShape } from '../game/wear';

const DIRT = vec3(0.3, 0.25, 0.19);

export class CarWear {
  readonly state: WearState;
  private readonly dirt = uniform(0);
  private readonly glassDirt = uniform(0);
  private readonly dents = uniformArray(
    Array.from({ length: MAX_DENTS }, () => new THREE.Vector4(0, -100, 0, 0)),
    'vec4',
  );
  private version = -1;

  private constructor(state: WearState) {
    this.state = state;
  }

  /** Measure the body, then hook dirt and dents into the car's materials (before the first render). */
  static attach(model: CarModel): CarWear {
    const root = model.body;
    root.updateMatrixWorld(true);
    const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
    const box = new THREE.Box3();
    const glassBox = new THREE.Box3();
    const tmp = new THREE.Box3();
    const m4 = new THREE.Matrix4();
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) return;
      mesh.geometry.computeBoundingBox();
      tmp.copy(mesh.geometry.boundingBox!).applyMatrix4(m4.multiplyMatrices(inv, mesh.matrixWorld));
      box.union(tmp);
      if (mesh.material === model.glass) glassBox.union(tmp);
    });
    const size = box.getSize(new THREE.Vector3());
    const c = box.getCenter(new THREE.Vector3());
    const shape: BodyShape = { cx: c.x, cy: c.y, cz: c.z, ax: size.x / 2, ay: size.y / 2, az: size.z / 2 };
    const wear = new CarWear(new WearState(shape));
    wear.hook(model, box, glassBox);
    return wear;
  }

  private hook(model: CarModel, box: THREE.Box3, glassBox: THREE.Box3): void {
    const p = positionLocal;
    const n1 = mx_noise_float(p.mul(3.1)).mul(0.5).add(0.5);
    const n2 = mx_noise_float(p.mul(13.0)).mul(0.5).add(0.5);
    const h = box.max.y - box.min.y;
    // More dirt low down and toward the back (rally cars wear the road on their lower half).
    const low = float(1).sub(smoothstep(box.min.y + h * 0.12, box.min.y + h * 0.9, p.y));
    const rear = float(1).sub(smoothstep(box.min.z, box.max.z, p.z)).mul(0.35).add(0.65);
    const mask = clamp(low.mul(rear).mul(n1.mul(0.6).add(0.55)).add(n2.mul(0.18)), 0, 1);
    const amount = select(frontFacing, clamp(this.dirt.mul(mask).mul(1.25), 0, 0.92), float(0));
    // Dents: summed inward displacement from up to MAX_DENTS spherical dents (radius 0.42 m).
    const dents = this.dents;
    const dentDepth = Fn(() => {
      const d = float(0).toVar();
      Loop(MAX_DENTS, ({ i }) => {
        const dn = dents.element(i) as unknown as ReturnType<typeof vec4>;
        const r = length(p.sub(dn.xyz));
        d.addAssign(dn.w.mul(float(1).sub(smoothstep(0, 0.42, r))));
      });
      return d;
    })();
    const dented = p.sub(normalLocal.mul(dentDepth));
    const scuff = select(frontFacing, clamp(dentDepth.mul(30), 0, 1).mul(step(0.45, n2)), float(0));
    type V3 = Parameters<typeof mix>[0];
    const wearColor = (base: V3) => mix(mix(base, DIRT, amount), mix(vec3(0.07, 0.065, 0.06), vec3(0.4, 0.37, 0.33), n1), scuff.mul(0.75));

    const paint = model.paint;
    if (paint) {
      if (paint.colorNode) paint.colorNode = wearColor(paint.colorNode as unknown as V3);
      paint.roughnessNode = mix(float(0.32), float(0.92), amount);
      paint.clearcoatNode = mix(float(1), float(0.15), amount);
      paint.positionNode = dented;
    }
    model.body.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || mesh.name !== 'batch:exterior') return;
      const m = mesh.material as THREE.MeshStandardNodeMaterial;
      const fcol = attribute('fcol', 'vec4');
      m.colorNode = wearColor(fcol.rgb);
      m.roughnessNode = mix(fcol.a, float(0.95), amount);
      m.positionNode = dented;
    });
    // Wheels: their own local frame, so just patchy dust on the rims and tyres.
    for (const w of model.wheels) {
      w.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh || mesh.name !== 'batch:wheel') return;
        const m = mesh.material as THREE.MeshStandardNodeMaterial;
        if ((m.userData as { wear?: boolean }).wear) return;
        (m.userData as { wear?: boolean }).wear = true;
        const fcol = attribute('fcol', 'vec4');
        m.colorNode = mix(fcol.rgb, DIRT, clamp(this.dirt.mul(n1.mul(0.5).add(0.5)), 0, 0.85));
        m.roughnessNode = mix(fcol.a, float(0.95), this.dirt);
      });
    }
    // Glass: a dusty film, heavier low down; on the windscreen the two wiper arcs stay clean.
    const g = model.glass;
    if (g && !glassBox.isEmpty()) {
      const gh = glassBox.max.y - glassBox.min.y;
      const wsZ = glassBox.max.z - 0.45;
      const pivotY = glassBox.min.y + gh * 0.08;
      // Wipers sweep two arcs from pivots at the base of the screen; the top band and the corners stay dirty.
      const reach = gh * 0.55;
      const arcL = length(p.xy.sub(vec3(0.33, pivotY, 0).xy));
      const arcR = length(p.xy.sub(vec3(-0.33, pivotY, 0).xy));
      const inArc = float(1).sub(smoothstep(reach * 0.88, reach, min(arcL, arcR))).mul(step(pivotY, p.y));
      const windscreen = step(wsZ, p.z);
      const film = clamp(this.glassDirt.mul(n1.mul(0.7).add(0.5)).mul(float(1).sub(windscreen.mul(inArc).mul(0.92))), 0, 0.85);
      // The physical glass material ignores an opacity node in this three.js version, so the dust
      // is its own thin layer drawn over the glass (same geometry, lit, translucent).
      const layer = new THREE.MeshStandardNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, roughness: 0.9, metalness: 0 });
      layer.colorNode = DIRT.mul(1.1);
      layer.opacityNode = film;
      layer.polygonOffset = true;
      layer.polygonOffsetFactor = -1;
      layer.polygonOffsetUnits = -1;
      const glassMeshes: THREE.Mesh[] = [];
      model.body.traverse((o) => {
        const mesh = o as THREE.Mesh;
        // Not the co-driver's helmet visor (it never gets dusty).
        let visor = false;
        for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p.name === 'driver') visor = true;
        if (mesh.isMesh && mesh.material === g && !visor) glassMeshes.push(mesh);
      });
      for (const gm of glassMeshes) {
        const dust = new THREE.Mesh(gm.geometry, layer);
        dust.name = 'glass-dust';
        dust.renderOrder = (gm.renderOrder ?? 0) + 1;
        dust.castShadow = false;
        dust.receiveShadow = false;
        gm.add(dust);
      }
    }
  }

  reset(): void {
    this.state.reset();
  }

  /** Push the state into the uniforms (cheap; dents only when they changed). */
  sync(): void {
    this.dirt.value = this.state.dirt;
    this.glassDirt.value = this.state.glassDirt;
    if (this.version === this.state.version) return;
    this.version = this.state.version;
    const arr = this.dents.array as THREE.Vector4[];
    for (let i = 0; i < MAX_DENTS; i++) {
      const d = this.state.dents[i];
      if (d) arr[i].set(d.x, d.y, d.z, d.depth);
      else arr[i].set(0, -100, 0, 0);
    }
  }
}
